(function () {
  'use strict';

  class SaSolutionsTabs extends HTMLElement {
    constructor() {
      super();
      this.tabs = [];
      this.cards = [];
      this.cardOffsets = [];
      this.activeCard = -1;
      this.headerH = 0;
      this._headerEl = null;
      this._headerObserver = null;
      this._ticking = false;
      this._raf = null;
      this._barRO = null;
      this._lastTarget = null;
      this._wheelLockedUntil = 0;
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onWheel = this._onWheel.bind(this);
      this._onTabClick = this._onTabClick.bind(this);
      this._onTabKeydown = this._onTabKeydown.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onHeaderState = this._onHeaderState.bind(this);
    }

    connectedCallback() {
      this.bar = this.querySelector('[data-solutions-sticky]');
      this.stack = this.querySelector('[data-solutions-stack]');
      this.track = this.querySelector('[data-solutions-track]');
      this.fill = this.querySelector('[data-solutions-progress]');
      this.scroller = this.querySelector('[data-solutions-tabs]');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-solutions-tab]'));
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-solutions-card]'));
      if (!this.stack || !this.track || !this.cards.length) return;

      this.stickyOffset = parseInt(this.dataset.stickyOffset, 10) || 0;
      this.designMode = !!(window.Shopify && window.Shopify.designMode);

      this.tabByBlock = {};
      this.tabs.forEach((tab) => {
        this.tabByBlock[tab.dataset.solutionsTab] = tab;
        tab.addEventListener('click', this._onTabClick);
        tab.addEventListener('keydown', this._onTabKeydown);
      });

      this._measureBar();
      this._measureCards();
      if ('ResizeObserver' in window) {
        this._barRO = new ResizeObserver(() => {
          this._measureBar();
          this._measureCards();
          this._requestUpdate();
        });
        if (this.bar) this._barRO.observe(this.bar);
        if (this.stack) this._barRO.observe(this.stack);
        if (this.track) this._barRO.observe(this.track);
      }

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      this.stack.addEventListener('wheel', this._onWheel, { passive: false });

      // Track the site header so the sticky intro and tabs can pin below it while it's shown
      // and rise back to the top when it hides — matching the header's own slide.
      this._headerEl = document.getElementById('header-component');
      if (this._headerEl && 'MutationObserver' in window) {
        this._headerObserver = new MutationObserver(this._onHeaderState);
        this._headerObserver.observe(this._headerEl, {
          attributes: true,
          attributeFilter: ['data-sticky-state'],
        });
      }

      if (this.designMode) {
        document.addEventListener('shopify:block:select', this._onBlockSelect);
        document.addEventListener('shopify:block:deselect', this._onBlockSelect);
      }

      this._update();
    }

    disconnectedCallback() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this._onTabClick);
        tab.removeEventListener('keydown', this._onTabKeydown);
      });
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this.stack.removeEventListener('wheel', this._onWheel);
      if (this._barRO) {
        this._barRO.disconnect();
        this._barRO = null;
      }
      if (this._headerObserver) {
        this._headerObserver.disconnect();
        this._headerObserver = null;
      }
      if (this.designMode) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this._onBlockSelect);
      }
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    // The header shows on scroll-up (data-sticky-state="active") and hides on
    // scroll-down ("idle"). When it toggles without a scroll event of our own,
    // re-run so the active tab / progress stay aligned to the sticky area's new position.
    _onHeaderState() {
      this._requestUpdate();
    }

    _headerActive() {
      return !!(this._headerEl && this._headerEl.dataset.stickyState === 'active');
    }

    // Resting sticky inset of the intro and tabs: header height (only while it's shown) plus
    // the merchant offset.
    _barTop(headerActive) {
      const active = headerActive === undefined ? this._headerActive() : headerActive;
      return (active ? this.headerH : 0) + this.stickyOffset;
    }

    _measureBar() {
      if (!this.bar) return;
      const h = Math.round(this.bar.getBoundingClientRect().height);
      if (h > 0) this.style.setProperty('--sa-st-sticky-h', h + 'px');
      // Cache the header height the theme publishes on <body>. The sticky area pins below
      // it while the header is shown; the scroll math adds it via _barTop().
      const hh = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
      this.headerH = isNaN(hh) ? 0 : hh;
    }

    // Natural (non-sticky) top of each card within the stack. offsetTop can't be
    // used here: on sticky-positioned cards it collapses to the pinned position
    // once scrolled, so every card would report the same offset. offsetHeight is
    // unaffected by sticky, and the cards are gapless flush children of the stack,
    // so cumulative heights give stable layout offsets for scroll targets.
    _measureCards() {
      let acc = 0;
      this.cardOffsets = this.cards.map((card) => {
        const top = acc;
        acc += card.offsetHeight;
        return top;
      });
    }

    // The final card reaches the end of its own sticky containing block before
    // the intro/tab area's containing block ends. Once that card starts moving
    // up, move the visual sticky area by the exact same distance. Keeping it
    // sticky preserves its layout space and prevents a jump in the card stack.
    _syncTerminalBar(line) {
      if (!this.bar || !this.cards.length) return;
      const finalCard = this.cards[this.cards.length - 1];
      const offset = Math.min(0, finalCard.getBoundingClientRect().top - line);
      this.bar.style.transform = offset < -1 ? 'translateY(' + Math.round(offset) + 'px)' : '';
    }

    _barH() {
      return this.bar ? this.bar.getBoundingClientRect().height : 0;
    }

    _activeLine(headerActive) {
      return this._barTop(headerActive) + this._barH();
    }

    _onScroll() {
      this._requestUpdate();
    }

    _onResize() {
      this._measureBar();
      this._measureCards();
      this._requestUpdate();
    }

    _activeCardAt(line) {
      let active = 0;
      for (let i = 0; i < this.cards.length; i++) {
        if (this.cards[i].getBoundingClientRect().top <= line + 2) active = i;
      }
      return active;
    }

    _isWheelInsideTrack(line) {
      const trackRect = this.track.getBoundingClientRect();
      return trackRect.top <= line + 2 && trackRect.bottom > line + 2;
    }

    _consumeWheel(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    _onWheel(e) {
      if (e.ctrlKey || e.shiftKey || !e.deltaY) return;
      const line = this._activeLine();
      if (!this._isWheelInsideTrack(line)) return;

      // Resolve the current card from its live position, not from the previous
      // scroll target, so an upward gesture cannot skip a card.
      const current = this._activeCardAt(line);
      const direction = e.deltaY > 0 ? 1 : -1;
      const last = this.cards.length - 1;

      // At the component's outer boundaries, hand scrolling back to the page.
      if ((current === last && direction > 0) || (current === 0 && direction < 0)) return;

      if (Date.now() < this._wheelLockedUntil) {
        this._consumeWheel(e);
        return;
      }

      if (window.lenis && window.lenis.isScrolling) {
        this._consumeWheel(e);
        return;
      }

      const target = current + direction;
      if (target < 0 || target >= this.cards.length) return;

      this._consumeWheel(e);
      this._wheelLockedUntil = Date.now() + 900;
      this._goToCard(target);
    }

    _requestUpdate() {
      if (this._ticking) return;
      this._ticking = true;
      this._raf = requestAnimationFrame(() => {
        this._ticking = false;
        this._update();
      });
    }

    _update() {
      if (!this.cards.length || !this.stack) return;
      let keepClickedTab = false;
      if (this._lastTarget) {
        const delta = Math.abs(window.scrollY - this._lastTarget.y);
        if (delta > 8 && Date.now() < this._lastTarget.until) keepClickedTab = true;
        else this._lastTarget = null;
      }
      const line = this._activeLine();

      this._syncTerminalBar(line);

      const active = this._activeCardAt(line);

      // Segmented progress. Each card owns 1/n of the track and the active card's
      // segment is always filled — so the first tab already shows 1/n at the very
      // top (it's selected by default) instead of an empty bar. Within a segment
      // the fill scrubs continuously toward the next as the card scrolls past.
      const n = this.cards.length;
      const scrolled = line - this.stack.getBoundingClientRect().top;
      let intra = 0;
      if (active < n - 1 && this.cardOffsets.length === n) {
        const segLen = this.cardOffsets[active + 1] - this.cardOffsets[active];
        if (segLen > 0) {
          intra = (scrolled - this.cardOffsets[active]) / segLen;
          if (intra < 0) intra = 0;
          else if (intra > 1) intra = 1;
        }
      }

      // Switch the highlighted tab at the halfway point of the transition to the next
      // card, instead of waiting until the card has fully slid into place.
      let activeTab = active;
      if (active < n - 1 && intra >= 0.5) activeTab = active + 1;
      if (!keepClickedTab && activeTab !== this.activeCard) this._setActive(activeTab);

      let p = (active + intra + 1) / n;
      if (p < 0) p = 0;
      else if (p > 1) p = 1;
      if (this.fill) this.fill.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    }

    _setActive(cardDomIndex) {
      this.activeCard = cardDomIndex;
      const blockId = this.cards[cardDomIndex]
        ? this.cards[cardDomIndex].dataset.solutionsCard
        : null;
      this.tabs.forEach((tab) => {
        const on = tab.dataset.solutionsTab === blockId;
        tab.classList.toggle('is-active', on);
        if (on) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
      });
      if (blockId != null && this.tabByBlock[blockId]) {
        this._scrollTabIntoView(this.tabByBlock[blockId]);
      }
    }

    _scrollTabIntoView(tab) {
      if (!this.scroller || !tab) return;
      if (this.scroller.scrollWidth <= this.scroller.clientWidth + 1) return;
      const left = tab.offsetLeft - (this.scroller.clientWidth - tab.offsetWidth) / 2;
      const max = this.scroller.scrollWidth - this.scroller.clientWidth;
      this.scroller.scrollTo({ left: Math.max(0, Math.min(left, max)), behavior: 'smooth' });
    }

    _cardDomIndexForBlock(blockId) {
      for (let i = 0; i < this.cards.length; i++) {
        if (this.cards[i].dataset.solutionsCard === String(blockId)) return i;
      }
      return -1;
    }

    _goToCard(cardDomIndex) {
      const card = this.cards[cardDomIndex];
      if (!card) return;
      // The stack itself isn't sticky, so its rect gives a stable absolute top.
      // Pair it with the pre-measured natural card offset (offsetTop is unreliable
      // on the sticky cards) so targets differ per card and scrolling up works.
      if (this.cardOffsets.length !== this.cards.length) this._measureCards();
      const stackTop = window.scrollY + this.stack.getBoundingClientRect().top;
      const cardTop = this.cardOffsets[cardDomIndex] || 0;
      const cardAbsTop = stackTop + cardTop;
      // The header hides on scroll-down and shows on scroll-up, which moves the
      // sticky area. Aim for the position it will settle at: scrolling up reveals the
      // header (it drops), scrolling down hides it (it rises). Otherwise the card
      // lands a header-height off and sits behind the sticky area or leaves a gap.
      const goingUp = cardAbsTop - (this.stickyOffset + this._barH()) < window.scrollY;
      const target = Math.max(0, Math.round(cardAbsTop - this._activeLine(goingUp) + 1));
      this._lastTarget = { index: cardDomIndex, y: target, until: Date.now() + 1200 };
      this._setActive(cardDomIndex);
      const reduce =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (window.lenis && typeof window.lenis.scrollTo === 'function') {
        window.lenis.scrollTo(target, reduce ? { immediate: true } : { duration: 1 });
      } else {
        window.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    _onTabClick(e) {
      e.preventDefault();
      const idx = this._cardDomIndexForBlock(e.currentTarget.dataset.solutionsTab);
      if (idx > -1) this._goToCard(idx);
    }

    _onTabKeydown(e) {
      const current = this.tabs.indexOf(e.currentTarget);
      if (current < 0) return;
      let next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (current + 1) % this.tabs.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
        next = (current - 1 + this.tabs.length) % this.tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = this.tabs.length - 1;
      if (next === null) return;
      e.preventDefault();
      this.tabs[next].focus();
      const idx = this._cardDomIndexForBlock(this.tabs[next].dataset.solutionsTab);
      if (idx > -1) this._goToCard(idx);
    }

    _onBlockSelect(e) {
      const card = e.target && e.target.closest && e.target.closest('[data-solutions-card]');
      let idx = -1;
      if (card) {
        idx = this.cards.indexOf(card);
      } else if (e.target && e.target.matches && e.target.matches('[data-solutions-tab]')) {
        idx = this._cardDomIndexForBlock(e.target.dataset.solutionsTab);
      }
      this.cards.forEach((c, i) =>
        c.classList.toggle('sa-st-editor-focus', i === idx && e.type === 'shopify:block:select')
      );
      if (idx > -1 && e.type === 'shopify:block:select') this._goToCard(idx);
    }
  }

  if (!customElements.get('sa-solutions-tabs')) {
    customElements.define('sa-solutions-tabs', SaSolutionsTabs);
  }
})();
